import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/toast";
import { usePermission } from "@/hooks/use-permission";
import { fetchAdminGroups, fetchAdminUsers, type AdminUser } from "@/lib/api";
import type { UserGroup } from "@/types";

function initials(name: string) {
  return name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
}

interface GroupMember {
  id: string;
  name: string;
}

export function GroupManagement() {
  const canManage = usePermission("admin:access");
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<GroupMember[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [manageId, setManageId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newMemberQuery, setNewMemberQuery] = useState("");
  const [newMembers, setNewMembers] = useState<string[]>([]);
  const [manageMemberQuery, setManageMemberQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchAdminGroups()
      .then((result) => {
        if (cancelled) return;
        if (result.error) {
          setError(`Failed to load groups: ${result.error}`);
        } else {
          setGroups(result.groups.map((g) => ({
            id: g.groupId,
            name: g.name,
            description: g.description || "No description provided.",
            memberIds: [],
            permissions: [],
          })));
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to connect to the admin API.");
        setLoading(false);
      });
    fetchAdminUsers().then((result) => {
      if (cancelled) return;
      if (!result.error) {
        setAllUsers(result.users.map((u: AdminUser) => ({
          id: u.principalId,
          name: u.displayName || u.email,
        })));
      }
    });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(
    () => groups.filter((g) => !query || g.name.toLowerCase().includes(query.toLowerCase())),
    [groups, query]
  );

  const manageGroup = groups.find((g) => g.id === manageId) ?? null;

  function findUser(id: string): GroupMember | undefined {
    return allUsers.find((u) => u.id === id);
  }

  function resetCreateForm() {
    setNewName("");
    setNewDesc("");
    setNewMemberQuery("");
    setNewMembers([]);
  }

  function handleCreate() {
    if (!newName.trim()) return;
    const id = `GRP-${String(groups.length + 1).padStart(3, "0")}`;
    const group: UserGroup = {
      id,
      name: newName.trim(),
      description: newDesc.trim() || "No description provided.",
      memberIds: newMembers,
      permissions: [],
    };
    setGroups((g) => [...g, group]);
    setCreateOpen(false);
    resetCreateForm();
    toast({
      variant: "success",
      title: `${group.name} created`,
      description: "Group created locally. Backend persistence for group management is not yet available.",
    });
  }

  function addNewMember() {
    const q = newMemberQuery.trim().toLowerCase();
    if (!q) return;
    const user = allUsers.find((u) => u.name.toLowerCase().includes(q) || u.id.toLowerCase() === q);
    if (!user) {
      toast({ variant: "info", title: "No matching user found", description: `No user matches "${newMemberQuery}".` });
      return;
    }
    if (!newMembers.includes(user.id)) setNewMembers((m) => [...m, user.id]);
    setNewMemberQuery("");
  }

  function addManageMember() {
    if (!manageGroup) return;
    const q = manageMemberQuery.trim().toLowerCase();
    if (!q) return;
    const user = allUsers.find((u) => u.name.toLowerCase().includes(q) || u.id.toLowerCase() === q);
    if (!user) {
      toast({ variant: "info", title: "No matching user found", description: `No user matches "${manageMemberQuery}".` });
      return;
    }
    if (manageGroup.memberIds.includes(user.id)) return;
    setGroups((gs) => gs.map((g) => (g.id === manageGroup.id ? { ...g, memberIds: [...g.memberIds, user.id] } : g)));
    setManageMemberQuery("");
  }

  function removeManageMember(userId: string) {
    if (!manageGroup) return;
    setGroups((gs) => gs.map((g) => (g.id === manageGroup.id ? { ...g, memberIds: g.memberIds.filter((id) => id !== userId) } : g)));
  }

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Identity</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Groups</span>
      </nav>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Group Management</h1>
          <p className="mt-1 text-sm text-muted">Organize users into groups with shared permissions.</p>
        </div>
      </div>

      {error && !loading && (
        <div className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {loading && (
        <div className="mb-4 text-center text-sm text-muted">Loading groups...</div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative w-[280px] max-w-full">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search groups..."
            className="h-[36px] w-full rounded-[8px] border border-line bg-surface pl-9 pr-3 text-[13px] text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
          />
        </div>
        {canManage && (
          <Button className="ml-auto gap-1" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" aria-hidden /> Create Group
          </Button>
        )}
      </div>

      {!loading && !error && filtered.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-surface px-4 py-10 text-center text-sm text-muted">
          {query ? "No groups match your search." : "No groups found."}
        </div>
      ) : !loading && !error && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((g) => (
            <div key={g.id} className="rounded-[10px] border border-line bg-surface p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-ink">{g.name}</div>
                {canManage && (
                  <button type="button" className="text-xs font-semibold text-primary hover:underline" onClick={() => setManageId(g.id)}>
                    Manage
                  </button>
                )}
              </div>
              <div className="mt-1 text-xs text-muted">{g.memberIds.length} member{g.memberIds.length === 1 ? "" : "s"}</div>
              <div className="mt-2 text-[11.5px] leading-relaxed text-muted">
                {g.description}
              </div>
              <div className="mt-3 flex -space-x-2">
                {g.memberIds.slice(0, 5).map((id) => {
                  const u = findUser(id);
                  return u ? (
                    <div key={id} className="rounded-full ring-2 ring-surface" title={u.name}>
                      <Avatar initials={initials(u.name)} size={28} fontSize={11} />
                    </div>
                  ) : null;
                })}
                {g.memberIds.length > 5 && (
                  <div className="grid size-7 place-items-center rounded-full bg-bg text-[10px] font-semibold text-muted ring-2 ring-surface">
                    +{g.memberIds.length - 5}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={createOpen}
        onOpenChange={(v) => {
          setCreateOpen(v);
          if (!v) resetCreateForm();
        }}
        title="Create Group"
        description="Group management mutations are not yet persisted to the backend."
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button disabled={!newName.trim()} onClick={handleCreate}>Create Group</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="grp-name">Group Name</label>
            <input
              id="grp-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Risk Management"
              className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="grp-desc">Description</label>
            <input
              id="grp-desc"
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Purpose of this group..."
              className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="grp-member">Add Members</label>
            <div className="flex gap-2">
              <input
                id="grp-member"
                type="text"
                value={newMemberQuery}
                onChange={(e) => setNewMemberQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNewMember(); } }}
                placeholder="Search by name or user id..."
                className="h-[36px] flex-1 rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none focus:border-primary"
              />
              <Button type="button" variant="secondary" onClick={addNewMember}>Add</Button>
            </div>
            {newMembers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {newMembers.map((id) => {
                  const u = findUser(id);
                  return (
                    <Badge key={id} variant="accent" dot={false} className="gap-1">
                      {u?.name ?? id}
                      <button type="button" aria-label={`Remove ${u?.name ?? id}`} onClick={() => setNewMembers((m) => m.filter((mid) => mid !== id))}>
                        <X className="size-3" aria-hidden />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Drawer>

      <Drawer
        open={!!manageGroup}
        onOpenChange={(v) => !v && setManageId(null)}
        title={manageGroup?.name ?? ""}
        description="Group membership changes are not yet persisted to the backend."
      >
        {manageGroup && (
          <div className="space-y-5">
            <p className="text-sm text-muted">{manageGroup.description}</p>
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Permissions</h4>
              {manageGroup.permissions.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {manageGroup.permissions.map((p) => (
                    <Badge key={p} variant="neutral" dot={false}>{p}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted">No permissions assigned.</p>
              )}
            </div>
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Members ({manageGroup.memberIds.length})</h4>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manageMemberQuery}
                  onChange={(e) => setManageMemberQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManageMember(); } }}
                  placeholder="Search by name or user id..."
                  className="h-[32px] flex-1 rounded-[6px] border border-line-strong bg-surface px-3 text-xs text-ink outline-none focus:border-primary"
                />
                <Button type="button" size="sm" variant="secondary" onClick={addManageMember}>Add</Button>
              </div>
              <ul className="mt-3 divide-y divide-line">
                {manageGroup.memberIds.map((id) => {
                  const u = findUser(id);
                  return (
                    <li key={id} className="flex items-center justify-between gap-2 py-2">
                      <div className="flex items-center gap-2">
                        <Avatar initials={initials(u?.name ?? id)} size={26} fontSize={10} />
                        <span className="text-sm text-ink">{u?.name ?? id}</span>
                      </div>
                      <button type="button" className="text-xs font-semibold text-danger hover:underline" onClick={() => removeManageMember(id)}>
                        Remove
                      </button>
                    </li>
                  );
                })}
                {manageGroup.memberIds.length === 0 && <li className="py-2 text-sm text-muted">No members yet.</li>}
              </ul>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
